<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import IconBase from '@/components/icon/IconBase.vue'
import IconReplace from '../icon/IconReplace.vue'
import IconClock from '../icon/IconClock.vue'
import PlayoutPreviewV2 from '@/components/TVPlayoutV2/PlayoutPreviewV2.vue'
import Badge from '@/components/Badge.vue'
import { date as formatDate, duration as formatDuration, cleanMediaName } from '@/filtersNew'

const props = defineProps({
	mediaPulse: {
		type: Object,
		default: null,
	},
})

const store = useStore()

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playlist = computed(() => store.getters['Playout/playlist'] || [])

const itemsCount = computed(() => playlist.value?.length || 0)

const playoutModeLabel = computed(() => {
	const mode = playoutConfig.value?.mode
	if (!mode) return ''
	return mode.charAt(0).toUpperCase() + mode.slice(1)
})

const isLive = computed(() => !!props.mediaPulse?.alive && !!playout.value?.enabled)

const nowTick = ref(Date.now())
let nowTickTimer = null
onMounted(() => {
	nowTickTimer = setInterval(() => {
		nowTick.value = Date.now()
	}, 1000)
})
onUnmounted(() => {
	if (nowTickTimer) clearInterval(nowTickTimer)
})

const isLoopMode = computed(() => playoutConfig.value?.mode === 'loop')

const playlistTotalDuration = computed(() => {
	if (!playlist.value?.length) return 0
	return playlist.value.reduce((acc, item) => acc + (item.endTime - item.startTime), 0)
})

const nowPlayingIndex = computed(() => {
	if (!isLive.value || !playlist.value?.length) return -1
	const now = nowTick.value

	if (isLoopMode.value) {
		const total = playlistTotalDuration.value
		if (total <= 0) return -1
		const start = playlist.value[0]?.startTime || now
		const offset = now - start
		const elapsed = offset >= 0 ? offset % total : 0
		let cumulative = 0
		for (let i = 0; i < playlist.value.length; i++) {
			const dur = playlist.value[i].endTime - playlist.value[i].startTime
			if (elapsed < cumulative + dur) return i
			cumulative += dur
		}
		return -1
	}

	return playlist.value.findIndex(
		(item) => item.startTime <= now && now < item.endTime,
	)
})

const nowPlaying = computed(() => {
	if (nowPlayingIndex.value < 0) return null
	return playlist.value[nowPlayingIndex.value] || null
})

const upNext = computed(() => {
	if (nowPlayingIndex.value < 0) return null
	const nextIndex = isLoopMode.value
		? (nowPlayingIndex.value + 1) % playlist.value.length
		: nowPlayingIndex.value + 1
	if (nextIndex === nowPlayingIndex.value) return null
	return playlist.value?.[nextIndex] || null
})

const hasPreview = computed(() => isLive.value && !!nowPlaying.value)

// Live progress of the currently-playing item.
const nowPlayingDurationMs = computed(() =>
	nowPlaying.value ? nowPlaying.value.endTime - nowPlaying.value.startTime : 0,
)
const nowPlayingElapsedMs = computed(() => {
	const item = nowPlaying.value
	if (!item) return 0
	const dur = item.endTime - item.startTime
	if (isLoopMode.value) {
		const total = playlistTotalDuration.value
		if (total <= 0) return 0
		const start = playlist.value[0]?.startTime || nowTick.value
		const offset = nowTick.value - start
		const elapsedInLoop = offset >= 0 ? offset % total : 0
		let cumulative = 0
		for (let i = 0; i < nowPlayingIndex.value; i++) {
			cumulative += playlist.value[i].endTime - playlist.value[i].startTime
		}
		return Math.min(Math.max(elapsedInLoop - cumulative, 0), dur)
	}
	return Math.min(Math.max(nowTick.value - item.startTime, 0), dur)
})
const nowPlayingProgressPct = computed(() => {
	const d = nowPlayingDurationMs.value
	if (!d) return 0
	return Math.min(100, Math.max(0, (nowPlayingElapsedMs.value / d) * 100))
})
const timeUntilNextMs = computed(() => {
	if (!upNext.value) return 0
	if (!isLoopMode.value && upNext.value.startTime) {
		return Math.max(0, upNext.value.startTime - nowTick.value)
	}
	return Math.max(0, nowPlayingDurationMs.value - nowPlayingElapsedMs.value)
})

// Live source names are shown as-is; upload/vod filenames are cleaned up.
const displayName = (item) => {
	if (!item) return ''
	if (item.sourceType === 'live') return item.sourceName || ''
	return cleanMediaName(item.sourceName)
}

const formatTime = (ts) => {
	if (!ts) return ''
	return formatDate(ts, 'HH:mm', true)
}
const formatItemDuration = (item) => {
	if (!item) return ''
	return formatDuration(item.endTime - item.startTime)
}
const formatClock = (ms) => formatDuration(Math.max(0, Math.round(ms)))

const playoutModeIcon = computed(() => {
	const mode = playoutConfig.value?.mode
	if (!mode) return null
	switch (mode) {
		case 'loop': return IconReplace
		case 'schedule': return IconClock
		default: return IconReplace
	}
})
</script>

<template>
	<section>
		<div class="bg-surface-2 rounded-lg p-3 grid grid-cols-1 md:grid-cols-[3fr_7fr] gap-4">
			<div class="rounded-t-md md:rounded-md overflow-hidden bg-black aspect-video -mx-3 md:-mx-0 -mt-3 md:-mt-0">
				<PlayoutPreviewV2 :media-pulse="mediaPulse" />
			</div>

			<div class="flex flex-col h-full min-h-0">
				<template v-if="hasPreview">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0">
						<div class="bg-surface-3 rounded-md p-4 flex flex-col border-l-2 border-success">
							<p class="text-xs text-success uppercase tracking-wide m-0 mb-2 inline-flex items-center gap-1.5 font-medium">
								<span class="relative flex h-1.5 w-1.5">
									<span class="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 animate-ping" />
									<span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
								</span>
								Now Playing
							</p>
							<h4 class="text-base text-white font-medium m-0 leading-snug line-clamp-2" :title="nowPlaying?.sourceName">
								{{ displayName(nowPlaying) }}
							</h4>
							<p v-if="!isLoopMode" class="text-tiny text-surface-7 mt-1 mb-0">
								{{ formatTime(nowPlaying?.startTime) }} – {{ formatTime(nowPlaying?.endTime) }}
							</p>
							<div class="mt-auto pt-3">
								<div class="h-1 rounded-full bg-surface-1 overflow-hidden">
									<div class="h-full bg-success rounded-full transition-[width] duration-1000 ease-linear"
										:style="{ width: nowPlayingProgressPct + '%' }" />
								</div>
								<div class="flex items-center justify-between mt-1.5 text-tiny text-surface-7 tabular-nums">
									<span>{{ formatClock(nowPlayingElapsedMs) }}</span>
									<span>{{ formatItemDuration(nowPlaying) }}</span>
								</div>
							</div>
						</div>
						<div class="bg-surface-3 rounded-md p-4 flex flex-col">
							<p class="text-xs text-surface-7 uppercase tracking-wide m-0 mb-2 font-medium">Up Next</p>
							<template v-if="upNext">
								<h4 class="text-base text-white font-medium m-0 leading-snug line-clamp-2" :title="upNext?.sourceName">
									{{ displayName(upNext) }}
								</h4>
								<p class="text-tiny text-surface-7 mt-1 mb-0">
									<template v-if="!isLoopMode">
										{{ formatTime(upNext?.startTime) }} – {{ formatTime(upNext?.endTime) }}
										<span class="mx-1">•</span>
									</template>
									{{ formatItemDuration(upNext) }}
								</p>
								<p class="mt-auto pt-3 mb-0 text-tiny text-surface-8 tabular-nums">
									Starts in {{ formatClock(timeUntilNextMs) }}
								</p>
							</template>
							<p v-else class="text-tiny text-surface-7 m-0">Nothing scheduled next</p>
						</div>
					</div>
				</template>
				<div v-else class="flex flex-col items-center justify-center text-center flex-1 py-6 min-h-0">
					<h4 class="text-base text-white font-medium m-0">Nothing to preview</h4>
					<p class="text-tiny text-surface-7 mt-1 mb-0 max-w-xs">
						Add content to the schedule to see a stream preview
					</p>
				</div>

				<div class="mt-auto pt-3 mb-1 flex justify-between items-center gap-3 shrink-0">
					<span class="text-xs text-surface-7 font-medium">
						{{ itemsCount }} {{ itemsCount === 1 ? 'item' : 'items' }}
					</span>
					<Badge v-if="playoutModeLabel" class="flex items-center gap-1" type="main" inline pill size="sm">
						<icon-base width="14" height="14">
							<component :is="playoutModeIcon" />
						</icon-base>
						{{ playoutModeLabel }}
					</Badge>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped></style>
